import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { api, fmt } from "../../../services/erpApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";
import { useToast } from "../../../components/admin/ToastContext";
import Modal from "../../../components/admin/Modal";
import { Plus, Loader2, CheckCircle2, XCircle, ChevronLeft, ChevronRight, HandCoins } from "lucide-react";

const PAGE_SIZE = 12;

const STATE_STYLES = {
  draft: "bg-slate-50 text-slate-600 border-slate-100",
  submitted: "bg-amber-50 text-amber-700 border-amber-100",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  refused: "bg-rose-50 text-rose-700 border-rose-100",
  paid: "bg-teal-50 text-teal-700 border-teal-100",
};

const ESSSalaryAdvancesPage = () => {
  const toast = useToast();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === "admin" || user?.is_special;
  const myEmployeeId = user?.employee_id;
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [form, setForm] = useState({
    employee_id: "",
    advance_amount: "",
    date_requested: new Date().toISOString().slice(0, 10),
    reason: "",
    deduction_mode: "monthly",
    installments: "",
    deduction_per_month: "",
  });

  const load = async () => {
    try {
      const params = { limit: 500 };
      if (!isAdmin && myEmployeeId) params.employee_id = myEmployeeId;
      const [advRes, empRes] = await Promise.all([
        api.salaryAdvances.list(params),
        isAdmin ? api.users.list().catch(() => []) : Promise.resolve([]),
      ]);
      setRows(advRes || []);
      setEmployees(empRes || []);
    } catch {
      setRows([]);
      setEmployees([]);
    }
    setLoaded(true);
  };

  useEffect(() => { load(); }, [myEmployeeId, isAdmin]);

  const empNameById = useMemo(() => {
    const m = {};
    (employees || []).forEach((e) => {
      if (e.employee_id != null) m[e.employee_id] = e.employee_name || e.login || String(e.employee_id);
    });
    return m;
  }, [employees]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((r) => r.state === statusFilter);
  }, [rows, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [statusFilter]);

  const stats = useMemo(() => {
    const pending = rows.filter((r) => r.state === "submitted").reduce((s, r) => s + (Number(r.advance_amount) || 0), 0);
    const approved = rows.filter((r) => r.state === "approved").reduce((s, r) => s + (Number(r.advance_amount) || 0), 0);
    const total = rows.reduce((s, r) => s + (Number(r.advance_amount) || 0), 0);
    return { count: rows.length, pending, approved, total };
  }, [rows]);

  const handleCreate = async () => {
    if (!form.advance_amount) { toast("Amount is required", "error"); return; }
    if (isAdmin && !form.employee_id) { toast("Employee is required", "error"); return; }
    setSubmitting(true);
    try {
      const installments = Number(form.installments);
      await api.salaryAdvances.create({
        employee_id: isAdmin ? parseInt(form.employee_id) : undefined,
        advance_amount: parseFloat(form.advance_amount),
        date_requested: form.date_requested,
        reason: form.reason || undefined,
        deduction_mode: form.deduction_mode,
        installments: installments > 0 ? installments : undefined,
        deduction_per_month: form.deduction_per_month ? parseFloat(form.deduction_per_month) : undefined,
        state: isAdmin ? "submitted" : undefined,
      });
      toast("Salary advance requested", "success");
      setModalOpen(false);
      setForm({ employee_id: "", advance_amount: "", date_requested: new Date().toISOString().slice(0, 10), reason: "", deduction_mode: "monthly", installments: "", deduction_per_month: "" });
      await load();
    } catch (e) {
      toast(e.message || "Create failed", "error");
    }
    setSubmitting(false);
  };

  const handleAction = async (row, state) => {
    setActingId(row.lewoo_uid || row.id);
    try {
      await api.salaryAdvances.update(row.lewoo_uid || row.id, {
        employee_id: row.employee_id,
        advance_amount: Number(row.advance_amount),
        date_requested: String(row.date_requested || "").slice(0, 10),
        reason: row.reason || undefined,
        deduction_mode: row.deduction_mode || "monthly",
        installments: row.installments ? Number(row.installments) : undefined,
        deduction_per_month: row.deduction_per_month ? Number(row.deduction_per_month) : undefined,
        state,
      });
      toast(`Advance ${state === "approved" ? "approved" : state === "refused" ? "refused" : "marked as paid"}`, "success");
      await load();
    } catch (e) {
      toast(e.message || "Action failed", "error");
    }
    setActingId(null);
  };

  if (!loaded) return <PageSkeleton />;

  const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", outline: "none", background: "#f8fafc", boxSizing: "border-box" };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  const actionButtons = (row) => {
    if (row.state === "submitted") {
      return (
        <>
          <button onClick={() => handleAction(row, "refused")} disabled={actingId === (row.lewoo_uid || row.id)} style={{ padding: "6px 10px", border: "1px solid #e11d48", borderRadius: "6px", background: "white", color: "#e11d48", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <XCircle size={13} /> Refuse
          </button>
          <button onClick={() => handleAction(row, "approved")} disabled={actingId === (row.lewoo_uid || row.id)} style={{ padding: "6px 10px", border: "none", borderRadius: "6px", background: "#16a34a", color: "white", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <CheckCircle2 size={13} /> Approve
          </button>
        </>
      );
    }
    if (row.state === "approved") {
      return (
        <button onClick={() => handleAction(row, "paid")} disabled={actingId === (row.lewoo_uid || row.id)} style={{ padding: "6px 10px", border: "none", borderRadius: "6px", background: "#0891b2", color: "white", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }}>
          <HandCoins size={13} /> Mark paid
        </button>
      );
    }
    return "—";
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salary Advances</h1>
          <p className="text-gray-500 text-sm mt-1">Manage employee salary advance requests and approvals</p>
        </div>
        <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-all shadow-sm">
          <Plus size={16} /> {isAdmin ? "New Advance" : "Request an Advance"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Requests", value: String(stats.count), sub: "Total requests", icon: "📋", color: "text-slate-600 bg-slate-100" },
          { label: "Pending Approval", value: fmt.moneyShort(stats.pending), sub: fmt.money(stats.pending), icon: "⏳", color: "text-amber-600 bg-amber-100" },
          { label: "Approved", value: fmt.moneyShort(stats.approved), sub: fmt.money(stats.approved), icon: "✅", color: "text-emerald-600 bg-emerald-100" },
          { label: "Total", value: fmt.moneyShort(stats.total), sub: fmt.money(stats.total), icon: "💰", color: "text-teal-600 bg-teal-100" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{s.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
                <p className="text-xs text-gray-400 mt-1">{s.sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${s.color}`}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-1 px-4 pt-4 border-b border-gray-100 flex-wrap">
          {[["all", "All"], ["submitted", "Submitted"], ["approved", "Approved"], ["paid", "Paid"], ["refused", "Refused"]].map(([key, label]) => (
            <button key={key} onClick={() => setStatusFilter(key)} className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${statusFilter === key ? "border-emerald-500 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
              {label}
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {key === "all" ? rows.length : rows.filter((r) => r.state === key).length}
              </span>
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">#</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">Employee</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">Amount</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">Reason</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden sm:table-cell">Requested</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">State</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-400">No salary advances found</td></tr>
              ) : (
                paginated.map((row, i) => (
                  <tr key={row.id} className="hover:bg-gray-50/50">
                    <td className="px-5 py-3.5 font-semibold text-gray-900">ADV-{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td className="px-5 py-3.5 text-gray-700 font-medium">{isAdmin ? (row.employee_name || empNameById[row.employee_id] || row.employee_id || "—") : "Vous"}</td>
                    <td className="px-5 py-3.5 font-semibold text-gray-900">{fmt.money(row.advance_amount)}</td>
                    <td className="px-5 py-3.5 text-gray-500 max-w-[220px] truncate">{row.reason || "—"}</td>
                    <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell">{fmt.date(row.date_requested)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATE_STYLES[row.state] || STATE_STYLES.draft}`}>
                        {(row.state || "draft").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div style={{ display: "flex", gap: "6px" }}>{isAdmin ? actionButtons(row) : "—"}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30"><ChevronLeft size={16} /></button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} className={`px-3 py-1.5 text-xs font-semibold rounded border ${p === page ? "bg-emerald-500 text-white border-emerald-500" : "border-gray-200 bg-white hover:bg-gray-50"}`}>{p}</button>
              ))}
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="p-1.5 rounded border border-gray-200 bg-white disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="New Salary Advance">
        <div className="space-y-4">
          {isAdmin ? (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Employee *</label>
              <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} style={inputStyle}>
                <option value="">Select employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_name || e.login || e.id}</option>)}
              </select>
            </div>
          ) : (
            <div className="px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700 font-medium">
              Requesting an advance for yourself
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Amount (FCFA) *</label>
              <input type="number" min="0" value={form.advance_amount} onChange={(e) => setForm({ ...form, advance_amount: e.target.value })} placeholder="100000" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date requested</label>
              <input type="date" value={form.date_requested} onChange={(e) => setForm({ ...form, date_requested: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Reason</label>
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Urgency, family, travel…" style={inputStyle} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Deduction mode</label>
              <select value={form.deduction_mode} onChange={(e) => setForm({ ...form, deduction_mode: e.target.value })} style={inputStyle}>
                <option value="monthly">Monthly</option>
                <option value="once">One time</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Installments</label>
              <input type="number" min="1" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} placeholder="3" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Per month</label>
              <input type="number" min="0" value={form.deduction_per_month} onChange={(e) => setForm({ ...form, deduction_per_month: e.target.value })} placeholder="33333" style={inputStyle} />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setModalOpen(false)} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={handleCreate} disabled={submitting} className="flex-1 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50">
              {submitting ? <><Loader2 size={16} className="animate-spin mr-2 inline" /> Submitting…</> : "Submit Request"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ESSSalaryAdvancesPage;
